/*
 * Panneau admin : statistiques agregees du tunnel de conversion.
 * Donnees strictement anonymes et commerciales : compteurs d'evenements,
 * nombre de parcours, bilans envoyes et rendez vous. AUCUNE donnee de sante.
 * Chargement des donnees : hooks/useFunnelStats. Calculs et CSV : lib/funnelStats.
 */

import { BarChart3, Download, FileDown, RefreshCw, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFunnelStats } from "@/hooks/useFunnelStats";
import { FUNNEL_STEPS, computeConversions } from "@/lib/funnelStats";
import { adminDownloadFunnelCsv, adminDownloadFunnelPdf } from "@/lib/api";

const sectionClass =
  "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-5";

/* Filtres de periode : fenetre glissante pour suivre les campagnes. */
const PERIODS = [
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "all", label: "Tout" },
];

const SummaryCard = ({ label, value, testId }) => (
  <div
    className="rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.35)] p-4"
    data-testid={testId}
  >
    <p className="font-heading text-2xl font-semibold text-[#235277]">{value}</p>
    <p className="mt-1 text-xs font-medium text-[#333333]">{label}</p>
  </div>
);

const FunnelRow = ({ label, count, maxCount, pct, testId }) => {
  const width = maxCount > 0 ? Math.max((count / maxCount) * 100, count > 0 ? 4 : 0) : 0;
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-[#333333]">{label}</span>
        <span className="flex items-center gap-2">
          {pct !== null && (
            <span
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
                pct >= 50
                  ? "bg-[#EAF7E7] text-[#114E0B]"
                  : pct >= 20
                    ? "bg-[#FDF6E7] text-[#5E5515]"
                    : "bg-[#FBEBEA] text-[#8C2F2B]"
              }`}
              data-testid={`${testId}-conversion`}
              title="Pourcentage de passage depuis l'étape précédente"
            >
              {pct} %
            </span>
          )}
          <span className="font-heading text-sm font-semibold text-[#060606]">{count}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-[rgba(35,82,119,0.08)] overflow-hidden">
        <div
          className="h-full rounded-full bg-[#235277]"
          style={{ width: `${width}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

const FunnelLoadingState = () => (
  <div className="space-y-3">
    <Skeleton className="h-16 w-full rounded-xl" />
    <Skeleton className="h-5 w-full rounded" />
    <Skeleton className="h-5 w-5/6 rounded" />
    <Skeleton className="h-5 w-4/6 rounded" />
  </div>
);

const FunnelErrorState = ({ onRetry }) => (
  <div
    className="flex items-start gap-3 rounded-xl bg-[#FDF6E7] border border-[rgba(94,85,21,0.25)] px-4 py-3"
    data-testid="admin-funnel-error"
  >
    <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-[#5E5515]" aria-hidden="true" />
    <div>
      <p className="text-xs font-medium text-[#5E5515]">
        Les statistiques n'ont pas pu être chargées.
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        data-testid="admin-funnel-retry-button"
        className="mt-2 rounded-lg border-[rgba(94,85,21,0.35)] text-[#5E5515] hover:bg-[rgba(94,85,21,0.08)] font-medium transition-colors duration-200"
      >
        Réessayer
      </Button>
    </div>
  </div>
);

const FIT_ORIENTATION_CARDS = [
  { key: "high_priority", label: "Prioritaire", accent: "text-[#235277]" },
  { key: "qualified", label: "Qualifiée", accent: "text-[#3D7A5A]" },
  { key: "nurture", label: "Nurture", accent: "text-[#8A6D3B]" },
];

/* Compteurs des orientations CYCLIQ FIT : pilotage des conversions (V2.1). */
const FitOrientationsPanel = ({ orientations }) => (
  <div
    className="rounded-2xl border border-[rgba(35,82,119,0.16)] bg-white p-4"
    data-testid="admin-fit-orientations-panel"
  >
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-xs font-semibold tracking-[0.14em] uppercase text-[#235277]">
        Orientations CYCLIQ FIT
      </p>
      <p className="text-xs text-[#9E9E9E]" data-testid="admin-fit-orientations-total">
        {orientations?.total ?? 0} questionnaire{(orientations?.total ?? 0) > 1 ? "s" : ""} sur la période
      </p>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-3">
      {FIT_ORIENTATION_CARDS.map(({ key, label, accent }) => (
        <div
          key={key}
          className="rounded-xl bg-[#FAFAF7] border border-[rgba(35,82,119,0.10)] px-3 py-3 text-center"
          data-testid={`admin-fit-orientation-${key}`}
        >
          <p className={`text-2xl font-semibold ${accent}`}>{orientations?.[key] ?? 0}</p>
          <p className="mt-1 text-[11px] text-[#333333]">{label}</p>
        </div>
      ))}
    </div>
    <p className="mt-3 text-[11px] leading-relaxed text-[#9E9E9E]">
      Compteurs commerciaux agrégés, sans aucune donnée de santé. Le score FIT
      individuel reste interne et n'apparaît jamais.
    </p>
  </div>
);

const FunnelResults = ({ data, events, maxCount, conversions }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <SummaryCard
        label="Parcours créés"
        value={data?.journeys_total ?? 0}
        testId="admin-funnel-journeys-total"
      />
      <SummaryCard
        label="Bilans envoyés par email"
        value={data?.bilans_sent ?? 0}
        testId="admin-funnel-bilans-sent"
      />
      <SummaryCard
        label="Relances envoyées"
        value={data?.relances_sent ?? 0}
        testId="admin-funnel-relances-sent"
      />
      <SummaryCard
        label="Rendez vous"
        value={data?.appointments ?? 0}
        testId="admin-funnel-appointments"
      />
    </div>

    <FitOrientationsPanel orientations={data?.fit_orientations} />

    <div className="space-y-3" data-testid="admin-funnel-events-list">
      {FUNNEL_STEPS.map((step) => (
        <FunnelRow
          key={step.key}
          label={step.label}
          count={events[step.key] || 0}
          maxCount={maxCount}
          pct={conversions[step.key]}
          testId={`admin-funnel-row-${step.key}`}
        />
      ))}
    </div>
  </>
);

/* ----- En tete de section : titre et actions d'export ----- */

const actionButtonClass =
  "rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-medium transition-colors duration-200";

const SectionHeader = ({ loading, error, data, pdfLoading, csvLoading, onExportCsv, onExportPdf, onReload }) => (
  <div className="flex items-start justify-between gap-3 flex-wrap">
    <div>
      <div className="flex items-center gap-2 text-[#235277]">
        <BarChart3 className="w-4 h-4" aria-hidden="true" />
        <h2 className="font-heading text-base font-semibold text-[#060606]">
          Statistiques du tunnel
        </h2>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
        Compteurs anonymes des étapes du parcours. Aucune réponse, aucun score et aucune
        donnée de santé ne figurent dans ces mesures.
      </p>
    </div>
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={onExportCsv}
        disabled={loading || error || !data || csvLoading}
        data-testid="admin-funnel-export-csv-button"
        className={actionButtonClass}
      >
        <Download className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
        {csvLoading ? "Préparation" : "Exporter CSV"}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onExportPdf}
        disabled={loading || error || !data || pdfLoading}
        data-testid="admin-funnel-export-pdf-button"
        className={actionButtonClass}
      >
        <FileDown className="mr-1.5 w-3.5 h-3.5" aria-hidden="true" />
        {pdfLoading ? "Préparation" : "Exporter PDF"}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onReload}
        disabled={loading}
        data-testid="admin-funnel-refresh-button"
        className={actionButtonClass}
      >
        <RefreshCw className={`mr-1.5 w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
        Actualiser
      </Button>
    </div>
  </div>
);

/* ----- Filtre de periode (7 jours, 30 jours, tout) ----- */

const PeriodFilter = ({ period, setPeriod }) => (
  <div
    className="inline-flex rounded-xl border border-[rgba(35,82,119,0.18)] bg-[rgba(226,246,255,0.35)] p-1"
    role="group"
    aria-label="Période des statistiques"
    data-testid="admin-funnel-period-filter"
  >
    {PERIODS.map((p) => (
      <button
        key={p.key}
        type="button"
        onClick={() => setPeriod(p.key)}
        data-testid={`admin-funnel-period-${p.key}-button`}
        aria-pressed={period === p.key}
        className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-[#235277] ${
          period === p.key
            ? "bg-[#235277] text-white"
            : "text-[#235277] hover:bg-[#E2F6FF]"
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

export const FunnelStatsSection = ({ adminKey }) => {
  const { data, loading, error, period, setPeriod, reload } = useFunnelStats(adminKey);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  const events = data?.events || {};
  const maxCount = Math.max(...FUNNEL_STEPS.map((s) => events[s.key] || 0), 0);
  const conversions = computeConversions(events);

  /* Telechargement d'un blob genere cote serveur (PDF ou CSV). */
  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /* Export PDF pret a partager : genere cote serveur pour la periode active. */
  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await adminDownloadFunnelPdf(adminKey, period);
      triggerDownload(blob, `STATISTIQUES_CYCLIQ_${period}.pdf`);
      toast.success("Rapport PDF téléchargé", {
        description: "Les statistiques de la période sont prêtes à partager.",
      });
    } catch (e) {
      toast.error("Export impossible", {
        description: "Le rapport PDF n'a pas pu être généré. Réessaie.",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  /* Export CSV : genere cote serveur, contenu identique au rapport PDF. */
  const exportCsv = async () => {
    setCsvLoading(true);
    try {
      const blob = await adminDownloadFunnelCsv(adminKey, period);
      triggerDownload(blob, `STATISTIQUES_CYCLIQ_${period}.csv`);
      toast.success("Export CSV téléchargé", {
        description: "Le fichier s'ouvre directement dans un tableur.",
      });
    } catch (e) {
      toast.error("Export impossible", {
        description: "Le fichier CSV n'a pas pu être généré. Réessaie.",
      });
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <section className={sectionClass} data-testid="admin-funnel-section">
      <SectionHeader
        loading={loading}
        error={error}
        data={data}
        pdfLoading={pdfLoading}
        csvLoading={csvLoading}
        onExportCsv={exportCsv}
        onExportPdf={exportPdf}
        onReload={reload}
      />

      <PeriodFilter period={period} setPeriod={setPeriod} />

      {loading && <FunnelLoadingState />}
      {!loading && error && <FunnelErrorState onRetry={reload} />}
      {!loading && !error && (
        <FunnelResults data={data} events={events} maxCount={maxCount} conversions={conversions} />
      )}
    </section>
  );
};
